import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Volume2, VolumeX, Play, ArrowRight } from 'lucide-react';
import { TID } from '../lib/testIds';

const REELS = [
  {
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/77gv5jik_ueta%20vid%201%20trim.mp4',
    index: '01',
    title: 'Events',
    category: 'Live · Recap',
    client: 'Mixed',
    year: '2025',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/b5wnwekn_ueta%20vid%202%20trim.mp4',
    index: '02',
    title: 'Weddings',
    category: 'Cinematic · Day',
    client: 'Private',
    year: '2025',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/kouyj2ey_ueta%20vid%203.mp4',
    index: '03',
    title: 'Brand Content',
    category: 'Brand · Performance',
    client: 'Studio',
    year: '2026',
  },
];

function Reel({ data, i }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => setMuted((m) => !m);
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <motion.article
      data-testid={TID.reel(i)}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      {/* Meta header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5 md:mb-6">
        <div className="flex items-baseline gap-5">
          <span className="font-display text-[#D8FF3A] text-4xl md:text-5xl font-black tracking-tighter neon-text-glow">
            {data.index}
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
              {data.category}
            </p>
            <h3
              className="font-display text-white font-bold tracking-tight"
              style={{ fontSize: 'clamp(24px, 2.6vw, 40px)', lineHeight: 1 }}
            >
              {data.title}
            </h3>
          </div>
        </div>
        <div className="flex items-end gap-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">
            <p className="text-white/35 mb-1">Client</p>
            <p>{data.client}</p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">
            <p className="text-white/35 mb-1">Year</p>
            <p>{data.year}</p>
          </div>
        </div>
      </div>

      {/* Video frame */}
      <div className="relative w-full bg-black border border-white/10 overflow-hidden group">
        <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover"
            src={data.src}
            autoPlay
            muted={muted}
            loop
            playsInline
            preload="metadata"
          />

          {/* subtle vignette only on hover for legibility of controls */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Controls */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
            <button
              onClick={togglePlay}
              aria-label={playing ? 'Pause video' : 'Play video'}
              className="inline-flex h-10 w-10 items-center justify-center bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              <Play size={14} className={playing ? 'opacity-40' : 'opacity-100'} />
            </button>
            <button
              data-testid={TID.reelMuteToggle(i)}
              onClick={toggleMute}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
              className="inline-flex h-10 w-10 items-center justify-center bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-[#D8FF3A] hover:text-black hover:border-[#D8FF3A] transition-colors"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>

          {/* small index marker top-left of video */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/80 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/15">
            <span className="h-1.5 w-1.5 bg-[#D8FF3A] rounded-full" />
            Reel {data.index}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Reels() {
  const navigate = useNavigate();
  return (
    <section id="reels" data-testid={TID.reelsRoot} className="relative bg-transparent">
      {/* Section intro band */}
      <div className="relative border-y border-white/10">
        <div className="px-6 md:px-14 py-10 md:py-14 flex flex-wrap items-baseline justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow">
              ◆ Selected work
            </span>
            <span className="hidden md:inline-block h-px w-12 bg-[#D8FF3A]/60" />
          </div>
          <motion.h2
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-white font-bold tracking-tight text-balance"
            style={{ fontSize: 'clamp(28px, 4vw, 56px)', lineHeight: 1 }}
          >
            Frames that move people.
          </motion.h2>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-white/50">
            <span className="text-[#D8FF3A]">Three reels</span> · {new Date().getFullYear()}
          </p>
        </div>
      </div>

      <div className="px-6 md:px-14 py-16 md:py-24">
        <div className="mx-auto max-w-6xl space-y-16 md:space-y-24">
          {REELS.map((r, idx) => (
            <Reel key={r.index} data={r} i={idx} />
          ))}
        </div>

        <div className="mx-auto max-w-6xl mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10 pt-10">
          <p className="text-sm md:text-base text-white/55 max-w-md">
            This is just a taste. Browse the full catalogue of films and photography in the
            portfolio.
          </p>
          <button
            data-testid={TID.portfolioViewAll}
            onClick={() => {
              navigate('/portfolio');
              window.scrollTo(0, 0);
            }}
            className="group inline-flex items-center gap-3 bg-[#D8FF3A] text-black px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white hover:neon-glow transition-all"
          >
            View full portfolio
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
}
